import React from "react";

import { DateType } from "@date-io/type";
import { Learner, RepeatFrequency, TimeSlot } from "@hubcapp/types";
import { repeatingLessonFrequencyNumberOptions } from "@hubcapp/components/Calendar/components/Modals/constants";

export type AllowedTravelTime = 0 | 15 | 30 | 45 | 60;
export type AllowedRepeatingLesson =
  | "NO_REPEAT"
  | "DAILY"
  | "WEEKLY"
  | "MONTHLY";

export type AllowedRepeatingLessonFrequencyNumber =
  | typeof repeatingLessonFrequencyNumberOptions[number]
  | null
  | undefined;

export type AddLessonFormState = {
  isValid: boolean;
  values: {
    learnerId?: string;
    type?: EventType;
    startDateTime?: Date;
    endDateTime?: Date;
    repeat?: RepeatFrequency;
    notes?: string;
    allDay?: boolean;
    test?: boolean;
    travelTime?: AllowedTravelTime;
    repeatingLesson?: AllowedRepeatingLesson;
    repeatingLessonFrequencyNumber?: AllowedRepeatingLessonFrequencyNumber;
  };
  touched: {
    learnerId?: boolean;
    type?: boolean;
    startDateTime?: boolean;
    endDateTime?: boolean;
    test?: boolean;
    repeat?: boolean;
    notes?: boolean;
  };
  errors: {
    learnerId?: string[];
    type?: string[];
    startDateTime?: string[];
    endDateTime?: string[];
    test?: string[];
    repeat?: string[];
    errors?: string[];
  };
};

export enum EventType {
  Unavailable = "UNAVAILABLE",
  PaidLesson = "PAID_LESSON",
  UnpaidLesson = "UNPAID_LESSON",
  NewLesson = "NEW_LESSON",
  Travel = "TRAVEL",
}

export type AddLessonModalProps = AddLessonFormState & {
  open?: boolean;
  learnersLoading?: boolean;
  createEventLoading?: boolean;
  learners: Pick<Learner, "id" | "name" | "allocations">[];
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleCancel: () => void;
  submitting?: boolean;
  handleEventTypeChange: (value: EventType) => void;
  showPreviewBookingModal: boolean;
  timeSlotsAvailability?: { timeSlotsAvailability: TimeSlot[] } | undefined;
  loadingTimeSlots: boolean;
  cancelPendingLessonsLoading: boolean;
  backPreviewBookingModalHandler: () => void;
  handleCreatePreBookedLessons: () => void;
  showSuccessBookingModal: string;
  lessonsNumber: number;
  convertPendingLessonsLoading: boolean;
};

export type UnavailableFormFieldsStateProviderProps = Pick<
  AddLessonFormState,
  "values" | "errors" | "touched"
> &
  Pick<AddLessonModalProps, "handleChange">;

export type UnavailableFormFieldsProps = UnavailableFormFieldsStateProviderProps & {
  startDate: DateType | null | undefined;
  handleStartDateChange: (date: DateType | null | undefined) => void;
  startTime: DateType | null | undefined;
  handleStartTimeChange: (date: DateType | null | undefined) => void;
  endTime: DateType | null | undefined;
  handleEndTimeChange: (date: DateType | null | undefined) => void;
  endDate: DateType | null | undefined;
  handleEndDateChange: (date: DateType | null | undefined) => void;
  allDay: boolean;
  handleAllDayChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  notes?: string;
  handleStartDateClick?: () => void;
  handleStartTimeClick?: () => void;
  handleEndDateClick?: () => void;
  handleEndTimeClick?: () => void;
  handleNotesClick?: () => void;
  handleRepeatFocus?: (value?: RepeatFrequency) => void;
};

export type LessonFormFieldsStateProviderProps = Pick<
  AddLessonFormState,
  "values"
> &
  Pick<AddLessonModalProps, "learners" | "handleChange" | "learnersLoading">;

export type LessonFormFieldsProps = LessonFormFieldsStateProviderProps & {
  startDate: DateType | null | undefined;
  handleStartDateChange: (date: DateType | null | undefined) => void;
  startTime: DateType | null | undefined;
  handleStartTimeChange: (date: DateType | null | undefined) => void;
  endTime: DateType | null | undefined;
  handleEndTimeChange: (date: DateType | null | undefined) => void;
  test: boolean | null | undefined;
  handleTestChange: (value: boolean) => void;
  travelTime: AllowedTravelTime;
  handleTravelTimeChange: (
    option: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => void;
  repeatingLesson: AllowedRepeatingLesson;
  handleRepeatingLessonChange: (
    option: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => void;
  disableRepeatingLesson?: boolean;
  disableRepeatingLessonChange: (value: boolean) => void;
  repeatingLessonFrequencyNumber: AllowedRepeatingLessonFrequencyNumber;
  handleRepeatingLessonFrequencyNumberChange: (
    option: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => void;
  selectedLearner?: Pick<Learner, "id" | "name" | "allocations">;
  lessonCost?: string;
  handleSearchLearnerEvent: () => void;
  handleDateEvent: () => void;
  handleStartTimeEvent: () => void;
  handleEndTimeEvent: () => void;
  handleTestEvent: () => void;
  handleLearnerChange: (value: Pick<Learner, "id" | "name"> | null) => void;
};
